import IconButton from "@material-ui/core/IconButton";
import MuiSnackbarContent from "@material-ui/core/SnackbarContent";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import classNames from "classnames";
import React from "react";
import { getClientConfiguration } from "../../../configuration";
import { SnackbarVariants } from "../constants";
import { SnackbarMessageIcon } from "./SnackbarMessageIcon";

const styles = (theme: Theme) =>
  createStyles({
    // Color match Snackbar Notifications:
    // https://github.com/iamhosseindhv/notistack/blob/master/src/SnackbarItem/SnackbarItem.tsx
    info: {
      backgroundColor: "#2196f3",
    },
    success: {
      backgroundColor: "#43a047",
    },
    warning: {
      backgroundColor: "#ff9800",
    },
    error: {
      backgroundColor: "#d32f2f",
    },
    close: {
      padding: theme.spacing(1),
      margin: `-${theme.spacing(1)}px 0`,
    },
    icon: {
      color: theme.palette.common.white,
      fontSize: 20,
    },
    iconVariant: {
      marginRight: theme.spacing(2),
    },
    messageContainer: {
      color: theme.palette.common.white,
      padding: `${theme.spacing(2)}px 0`,
      width: `calc(100% - ${theme.spacing(5)}px)`,
    },
    actionContainer: {
      paddingLeft: 0,
    },
    message: {
      display: "flex",
    },
    messageText: {
      "& :first-child": {
        marginTop: 0,
      },
      "& :last-child": {
        marginBottom: 0,
      },
    },
    messageTitle: {
      color: theme.palette.common.white,
      marginTop: 0,
    },
    blockquotedMessage: {
      borderLeft: `solid 3px ${theme.palette.common.white}`,
      paddingLeft: theme.spacing(1),
      fontStyle: "italic",
    },
    link: {
      color: theme.palette.common.white,
      "&:hover": {
        textDecoration: "underline",
      },
    },
  });

interface SnackbarMessageContentProps extends WithStyles<typeof styles> {
  variant: SnackbarVariants;
  message: React.ReactNode;
  className?: string;
  onClose: (event: any) => void;
}

export const SnackbarMessageContentComponent = (props: SnackbarMessageContentProps) => {
  const { message: rawMessage, variant, classes, className, onClose, ...other } = props;

  const clientConfiguration = getClientConfiguration();

  let message = rawMessage;
  if (variant === "error") {
    message = (
      <>
        {message ? (
          <>
            <p>The following error occurred:</p>
            <p className={classes.blockquotedMessage}>{message}</p>
          </>
        ) : (
          <p>An unspecified error occurred.</p>
        )}
        <p>
          Please retry your last operation. If the error persists, please contact{" "}
          {clientConfiguration.supportEmailAddress ? (
            <a className={classes.link} href={`mailto:${clientConfiguration.supportEmailAddress}`}>
              {clientConfiguration.supportEmailAddress}
            </a>
          ) : (
            "an Administrator"
          )}{" "}
          for assistance.
        </p>
      </>
    );
  }

  return (
    <MuiSnackbarContent
      classes={{ message: classes.messageContainer, action: classes.actionContainer }}
      className={classNames(classes[variant], className)}
      aria-describedby="snackbar-message"
      message={
        <div id="snackbar-message" className={classes.message}>
          <SnackbarMessageIcon variant={variant} className={classNames(classes.icon, classes.iconVariant)} />
          <div className={classes.messageText}>{message}</div>
        </div>
      }
      action={
        <IconButton key="close" aria-label="Close" color="inherit" className={classes.close} onClick={onClose}>
          <CloseIcon className={classes.icon} />
        </IconButton>
      }
      {...other}
    />
  );
};

export const SnackbarMessageContent = withStyles(styles)(SnackbarMessageContentComponent);
